import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import Seo from "../components/Seo";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// Custom Google OAuth callback (user's own Google Cloud project).
// Google redirects here with `?code=...`. We POST the code to the backend
// which exchanges it for tokens + userinfo and sets the app JWT cookie.
export default function GoogleCallback() {
  const nav = useNavigate();
  const loc = useLocation();
  const { refresh } = useAuth();
  const ran = useRef(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    // Runs EXACTLY ONCE, enforced by the `ran` ref. An OAuth authorization
    // code is single-use: a second exchange attempt fails with invalid_grant
    // and strands the user. `code` is read from the URL inside this effect
    // rather than captured from an outer scope, so there is no stale closure
    // here. Do not add further dependencies to "satisfy" a linter.
    if (ran.current) return;
    ran.current = true;

    const qs = new URLSearchParams(loc.search);
    const code = qs.get("code");
    const errParam = qs.get("error");
    if (errParam) { setErr(errParam); setTimeout(() => nav("/login", { replace: true }), 1500); return; }
    if (!code) { nav("/login", { replace: true }); return; }

    // Redirect URI MUST exactly match the one used at the authorize step.
    const redirect_uri = window.location.origin + "/auth/google";

    (async () => {
      try {
        const { data } = await api.post("/auth/google/exchange", { code, redirect_uri });
        await refresh();
        const role = data?.user?.role;
        const to = (role === "admin" || role === "subadmin") ? "/admin"
          : role === "trainer" ? "/trainer" : "/learner";
        nav(to, { replace: true });
      } catch (e) {
        console.error(e);
        setErr(e.response?.data?.detail || e.message || "Google sign-in failed");
        setTimeout(() => nav("/login", { replace: true }), 1800);
      }
    })();
  }, [nav, loc.search, refresh]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <Seo title="Signing In with Google – DriveTerrain" description="Completing Google sign-in." robots="noindex, nofollow" hrefLangs={false} />
      <div className="text-center">
        <div className="text-sm uppercase tracking-widest text-muted-foreground" data-testid="google-callback">
          {err ? "Sign-in failed" : "Signing you in with Google…"}
        </div>
        {err && <div className="mt-2 text-sm text-destructive" data-testid="google-callback-error">{err}</div>}
      </div>
    </div>
  );
}
